import { normalizePhone } from './phone.js';

export type ClientDateParts = {
  day: string;
  month: string;
  year: string;
};

export type ClientBirthDateResult =
  | { ok: true; value: string | null }
  | {
      ok: false;
      errorKey: 'validation.dateFull' | 'client.invalidDate' | 'validation.birthDateFuture';
    };

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isValidDateParts(day: number, month: number, year: number): boolean {
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
  if (year < 1900 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1) return false;

  const monthLengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= monthLengths[month - 1]!;
}

function isBirthDateAfterToday(day: number, month: number, year: number): boolean {
  const today = new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const candidate = new Date(year, month - 1, day);
  return candidate.getTime() > t0.getTime();
}

/**
 * Вставка из буфера: DD.MM.YYYY, DD/MM/YYYY или 8 цифр DDMMYYYY.
 */
export function parseClientBirthDatePaste(raw: string): ClientDateParts | null {
  const s = raw.trim();
  const m = /^(\d{2})[-./](\d{2})[-./](\d{4})$/.exec(s);
  if (m) {
    return { day: m[1], month: m[2], year: m[3] };
  }
  const digits = s.replace(/\D/g, '');
  if (digits.length === 8) {
    return {
      day: digits.slice(0, 2),
      month: digits.slice(2, 4),
      year: digits.slice(4, 8),
    };
  }
  return null;
}

type CompleteBirthCheck =
  | { ok: true }
  | { ok: false; errorKey: 'client.invalidDate' | 'validation.birthDateFuture' };

function checkCompleteBirthDate(parts: ClientDateParts): CompleteBirthCheck | null {
  if (!(parts.day.length === 2 && parts.month.length === 2 && parts.year.length === 4)) {
    return null;
  }
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (!isValidDateParts(day, month, year)) {
    return { ok: false, errorKey: 'client.invalidDate' };
  }
  if (isBirthDateAfterToday(day, month, year)) {
    return { ok: false, errorKey: 'validation.birthDateFuture' };
  }
  return { ok: true };
}

/** Для полей ввода: полная дата невалидна / в будущем, иначе null (в т.ч. пока дата неполная). */
export function getClientBirthDateFieldErrorKey(parts: ClientDateParts): 'client.invalidDate' | 'validation.birthDateFuture' | null {
  const r = checkCompleteBirthDate(parts);
  if (r === null) return null;
  if (!r.ok) return r.errorKey;
  return null;
}

/**
 * Единый формат хранения полного имени клиента.
 * Исторически в проекте клиент сохраняется как "Фамилия Отчество Имя".
 * Не меняем порядок здесь, чтобы все слои использовали одну и ту же схему.
 */
export function buildClientStoredName(parts: {
  firstName: string;
  lastName: string;
  middleName?: string;
}): string {
  return [parts.lastName.trim(), (parts.middleName || '').trim(), parts.firstName.trim()]
    .filter(Boolean)
    .join(' ')
    .trim();
}

/** Legacy: 10 цифр РФ без +7 → E.164. Новый UI отдаёт уже E.164 — передавай как есть. */
export function buildClientPhoneFromDigits(phoneDigits: string): string {
  const digits = phoneDigits.replace(/\D/g, '');
  return normalizePhone(digits, 'RU');
}

export function buildClientBirthDate(parts: ClientDateParts): ClientBirthDateResult {
  const hasAny = parts.day || parts.month || parts.year;
  if (!hasAny) return { ok: true, value: null };

  const complete = checkCompleteBirthDate(parts);
  if (complete === null) {
    return { ok: false, errorKey: 'validation.dateFull' };
  }
  if (!complete.ok) {
    return { ok: false, errorKey: complete.errorKey };
  }

  return {
    ok: true,
    value: `${parts.year}-${parts.month}-${parts.day}`,
  };
}
